import { z } from "zod";
import apiClient from "@/api/client";
import type { TradeMateTradeRecord } from "@/utils/mapRecentTrades";

const tradeMateAccountSchema = z.object({
  id: z.union([z.string(), z.number()]),
  accountNumber: z.string().nullable().optional(),
  accountId: z.string().nullable().optional(),
  login: z.string(),
  accountName: z.string(),
  platform: z.string(),
  server: z.string().nullable().optional(),
  password: z.string().nullable().optional(),
  status: z.string().nullable().optional(),
  balance: z.union([z.number(), z.string()]).nullable().optional(),
  currentBalance: z.union([z.number(), z.string()]).nullable().optional(),
  currentEquity: z.union([z.number(), z.string()]).nullable().optional(),
  liveUpdatedAt: z.string().nullable().optional(),
  challengeType: z.string().nullable().optional(),
  challengePhase: z.number().nullable().optional(),
  createdAt: z.string().nullable().optional(),
  updatedAt: z.string().nullable().optional(),
});

// Meta-shaped account entry: { mtAccount, metaStats, trades, analysis } — the
// same structure /api/meta/get-accounts returns. Kept intentionally loose.
const tradeMateAccountEntrySchema = z.object({
  mtAccount: z.record(z.any()),
  metaStats: z.any().nullable().optional(),
  trades: z.array(z.any()).optional(),
  analysis: z.array(z.any()).optional(),
});

const tradeMateAccountsResponseSchema = z.object({
  success: z.boolean(),
  data: z.union([
    z.array(tradeMateAccountSchema),
    z.object({
      accounts: z.array(tradeMateAccountSchema),
    }),
    // New: login-keyed map of meta-shaped entries.
    z.object({
      accounts: z.record(tradeMateAccountEntrySchema),
    }),
  ]),
  message: z.string().optional(),
});

type TradeMateAccountsResponse = z.infer<typeof tradeMateAccountsResponseSchema>;

export type TradeMateAccount = z.infer<typeof tradeMateAccountSchema>;
export type TradeMateAccountRecord = TradeMateAccount;

export type SwitchPlatformResponse = {
  success: boolean;
  message?: string;
  error?: unknown;
  code?: string;
  data?: {
    previousAccount: {
      accountId: string | null;
      login: string;
      server: string;
      platform: string;
      balance: number;
      challengeType: string | null;
      status: string;
      sourceTable: string;
    };
    tradeMateAccount: {
      accountId: string;
      login: string;
      password: string;
      server: string;
      platform: string;
      balance: number;
      challengeType: string | null;
      tradeMateUrl?: string;
    };
  };
};

export async function getMyTradeMateAccounts(): Promise<TradeMateAccount[]> {
  const response = await apiClient.get<TradeMateAccountsResponse>("/trade-mate/accounts");
  const parsed = tradeMateAccountsResponseSchema.parse(response.data);
  if (Array.isArray(parsed.data)) return parsed.data;
  const accounts = parsed.data.accounts;
  if (Array.isArray(accounts)) return accounts;
  // Meta-shaped map: derive flat summaries from each entry's mtAccount.
  return Object.values(accounts).map((entry: any) => ({ ...entry.mtAccount }));
}

export async function getMyTradeMateTrades(params?: {
  status?: "OPEN" | "CLOSED";
  accountId?: string;
  limit?: number;
}): Promise<TradeMateTradeRecord[]> {
  const response = await apiClient.get<{ success: boolean; data?: TradeMateTradeRecord[]; message?: string }>(
    "/trade-mate/trades",
    { params },
  );
  return response.data.data ?? [];
}

export const tradeMate = {
  getMyAccounts: async (
    _userId?: string,
    options?: { skipCatchUp?: boolean },
  ): Promise<TradeMateAccountsResponse> => {
    const response = await apiClient.get<TradeMateAccountsResponse>("/trade-mate/accounts", {
      params: {
        ...(options?.skipCatchUp ? { skipCatchUp: 1 } : {}),
      },
    });
    return tradeMateAccountsResponseSchema.parse(response.data);
  },
  changePassword: async (login: string, newPassword: string) => {
    const response = await apiClient.post("/trade-mate/change-password", {
      login,
      newPassword,
    });
    return response.data;
  },
  // Retires an MT4/MT5 account and assigns a Trade Mate account of the same
  // balance. Allowed once per MT account — the backend rejects a repeat with 409.
  switchPlatform: async (params: {
    login: string;
    server?: string;
    platform?: string;
  }): Promise<SwitchPlatformResponse> => {
    const response = await apiClient.post<SwitchPlatformResponse>(
      "/trade-mate/switch-platform",
      params,
    );
    return response.data;
  },
  getMyTrades: getMyTradeMateTrades,
};
